/**
 * useTranscriptManager Hook
 * 
 * Handles transcript processing and message state management:
 * - Process incoming user transcripts (direct item_id lookup)
 * - Process AI response deltas and completion
 * - Hallucination/prompt fragment filtering
 * - Message history management
 * - Session tracking integration
 * 
 * REFACTORED (2026-01): Removed speech correlator dependency.
 * 
 * Old flow (broken): transcript arrives -> correlate item_id to speechId -> update placeholder
 * New flow (fixed): transcript arrives -> direct lookup by item_id -> update message
 * 
 * Messages are created by useSpeechLifecycle when SERVER_SPEECH_STARTED arrives,
 * so the item_id is already the message ID. No correlation needed!
 */

import { useCallback, useRef } from 'react';
import type { SessionTrackingCallbacks } from '../../types/callbacks';
import { logger } from '../../utils/logger';
import {
  getUnintelligibleText,
  looksLikeHallucination,
  looksLikePromptFragment,
} from './constants';
import type { SendFunction } from './types';

// ============================================================================
// Types
// ============================================================================

export interface UseTranscriptManagerParams {
  send: SendFunction;
  boundSessionTrackingIdRef: React.MutableRefObject<string | undefined>;
  aiResponseStartedTimestampRef: React.MutableRefObject<number | null>;
  sessionTracking?: SessionTrackingCallbacks;
}

export interface UseTranscriptManagerReturn {
  // Handlers for WebSocket events
  handleTranscriptReceived: (transcript: string, openAiItemId?: string) => void;
  handleAIResponseDelta: (delta: string) => void;
  handleAIResponseDone: (text: string) => void;
  
  // Actions
  clearTranscript: () => void;
  clearError: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useTranscriptManager({
  send,
  boundSessionTrackingIdRef,
  aiResponseStartedTimestampRef,
  sessionTracking,
}: UseTranscriptManagerParams): UseTranscriptManagerReturn {

  // Local ref to accumulate AI response text synchronously
  // XState batches events, so using context.aiResponseInProgress + delta can lose deltas
  const aiResponseTextRef = useRef<string>('');

  const stripIdentityProtocolLeak = useCallback((text: string): string => {
    const trimmed = text.trim();
    if (trimmed === '') {
      return trimmed;
    }

    const lower = trimmed.toLowerCase();
    if (lower.startsWith('{"my_role"') || lower.startsWith('{ "my_role"')) {
      return '';
    }
    if (!lower.startsWith('asserting identity:')) {
      return trimmed;
    }

    const punctuationCuts = ['. ', '? ', '! '];
    let cutIndex = -1;
    for (const marker of punctuationCuts) {
      const idx = trimmed.indexOf(marker);
      if (idx !== -1 && (cutIndex === -1 || idx < cutIndex)) {
        cutIndex = idx;
      }
    }

    if (cutIndex === -1) {
      return '';
    }
    return trimmed.substring(cutIndex + 2).trim();
  }, []);

  // Handle incoming user transcript
  // REFACTORED: Direct item_id lookup - no correlation needed!
  const handleTranscriptReceived = useCallback((transcript: string, openAiItemId?: string) => {
    const trimmedTranscript = transcript?.trim() || '';
    const unintelligibleText = getUnintelligibleText();
    
    // item_id is required - message was created by SERVER_SPEECH_STARTED with this ID
    if (!openAiItemId) {
      logger.warn('[WebSocket] ⚠️ Transcript without item_id - ignoring (should not happen)');
      return;
    }
    
    // Handle empty transcripts - remove the message (user didn't say anything meaningful)
    if (trimmedTranscript.length === 0) {
      logger.warn('[WebSocket] ⚠️ Empty transcript received - removing message');
      
      // Remove the message and notify state machine
      send({ type: 'REMOVE_MESSAGE', id: openAiItemId });
      send({ type: 'EMPTY_TRANSCRIPT', itemId: openAiItemId });
      
      return;
    }

    // Detect and handle hallucinations/prompt fragments
    let finalTranscript = trimmedTranscript;
    
    if (looksLikeHallucination(trimmedTranscript)) {
      logger.warn('[WebSocket] 🚨 Garbage transcript detected - replacing with unintelligible:', {
        transcriptLength: trimmedTranscript.length,
        replacement: unintelligibleText,
        itemId: openAiItemId
      });
      finalTranscript = unintelligibleText;
    } else if (looksLikePromptFragment(trimmedTranscript)) {
      logger.warn('[WebSocket] 🚨 Prompt fragment detected - replacing with unintelligible:', {
        transcriptLength: trimmedTranscript.length,
        replacement: unintelligibleText,
        itemId: openAiItemId
      });
      finalTranscript = unintelligibleText;
    } else {
      logger.debug('[WebSocket] ✅ Valid transcript received:', {
        transcriptLength: trimmedTranscript.length,
        itemId: openAiItemId,
      });
    }

    // Handle empty final transcript (after hallucination replacement)
    if (!finalTranscript || finalTranscript.trim() === '') {
      logger.warn('[WebSocket] ⚠️ Final transcript is empty - removing message');
      send({ type: 'REMOVE_MESSAGE', id: openAiItemId });
      send({ type: 'EMPTY_TRANSCRIPT', itemId: openAiItemId });
      return;
    }

    // Update the message directly by item_id - no correlation needed!
    send({ 
      type: 'UPDATE_MESSAGE', 
      id: openAiItemId, 
      updates: { 
        text: finalTranscript, 
        rawTranscript: trimmedTranscript,
      } 
    });
    
    // Dispatch transcript received event (for state machine transition)
    send({ type: 'TRANSCRIPT_RECEIVED', text: finalTranscript, itemId: openAiItemId, rawTranscript: trimmedTranscript });
    
    // Log to session tracking (if callbacks provided)
    if (boundSessionTrackingIdRef.current && sessionTracking?.logMessage) {
      sessionTracking.logMessage({
        text: finalTranscript,
        timestamp: Date.now(),
        speaker: 'user',
        id: openAiItemId
      });
    }
  }, [send, boundSessionTrackingIdRef, sessionTracking]);

  // Handle AI response delta (streaming text)
  const handleAIResponseDelta = useCallback((delta: string) => {
    // Capture timestamp on first delta AND reset accumulator
    if (aiResponseStartedTimestampRef.current == null) {
      aiResponseStartedTimestampRef.current = Date.now();
      aiResponseTextRef.current = '';
    }
    
    // Accumulate text locally in ref (synchronous, no batching issues)
    aiResponseTextRef.current += delta;
    
    // Dispatch event with FULL accumulated text (not just delta)
    send({ type: 'AI_RESPONSE_DELTA', delta, fullText: aiResponseTextRef.current });
  }, [send, aiResponseStartedTimestampRef]);

  // Handle AI response completion
  const handleAIResponseDone = useCallback((text: string) => {
    const sanitizedText = stripIdentityProtocolLeak(text);
    // Log to session tracking (if callbacks provided)
    if (boundSessionTrackingIdRef.current && sessionTracking?.logMessage) {
      sessionTracking.logMessage({
        text: sanitizedText,
        timestamp: Date.now(),
        speaker: 'ai'
      });
    }
    
    // Dispatch event to state machine
    send({ type: 'AI_RESPONSE_DONE', text: sanitizedText });
    
    // Clear local accumulator for next response
    aiResponseTextRef.current = '';
    
    // Clear timestamp for next response
    aiResponseStartedTimestampRef.current = null;
  }, [send, boundSessionTrackingIdRef, aiResponseStartedTimestampRef, sessionTracking, stripIdentityProtocolLeak]);

  // Clear transcript history
  const clearTranscript = useCallback(() => {
    send({ type: 'CLEAR_MESSAGES' });
  }, [send]);

  // Clear error state
  const clearError = useCallback(() => {
    send({ type: 'CLEAR_ERROR' });
  }, [send]);

  return {
    handleTranscriptReceived,
    handleAIResponseDelta,
    handleAIResponseDone,
    clearTranscript,
    clearError,
  };
}
